import React, {ImgHTMLAttributes, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import isChromatic from 'chromatic'
import {Radius} from 'types/Theme'

import {
  ImageProvider,
  ImageResizingType
} from '@vio/core/src/staticContent/types/ImageProvider'

import {cssTheme} from '../../../themes'
import {ImagePlaceholder} from '../helpers/ImagePlaceholder'
import {SkeletonLoader} from '../SkeletonLoader/SkeletonLoader'

export type SizeType =
  | 'lightbox'
  | 'extraLarge'
  | 'large'
  | 'main'
  | 'medium'
  | 'small'
  | 'mobileLarge'
  | 'mobileMedium'
  | 'mobileSmall'
  | 'gridPrimary'
  | 'gridSecondary'
  | 'thumbnail'
  | 'desktopCard'
  | 'mobileCard'

export const SIZES: Record<SizeType, [number, number]> = {
  lightbox: [840, 635],
  extraLarge: [740, 393],
  large: [615, 340],
  main: [292, 284],
  medium: [59, 59],
  small: [38, 38],
  mobileLarge: [620, 176],
  mobileMedium: [620, 152],
  mobileSmall: [126, 152],
  gridPrimary: [456, 330],
  gridSecondary: [228, 162],
  thumbnail: [180, 120],
  desktopCard: [392, 336],
  mobileCard: [128, 182]
}

interface PropsType extends ImgHTMLAttributes<HTMLImageElement> {
  /** URL of the image */
  url: string
  /** Container element id */
  id?: string
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Apply specified border-radius styling. */
  borderRadius?: Radius
  /** Pass through style to allow styles overrides */
  style?: React.CSSProperties
  /** Whether to display the image as the full width of its parent */
  fullWidth?: boolean
  /** Size preset, used for image source and also component size unless width/height is specified */
  size?: SizeType
  /** Override the height of the component. Does not work when "isResponsive" is "true" */
  height?: number
  /** Override the width of the component. */
  width?: number
  /** Whether to display the component as the full width and height of its parent */
  isResponsive?: boolean
  /** How to position the img element in the container (object-fit), default: 'cover' */
  backgroundSize?: 'cover' | 'contain'
  /** Provider used to manage resizing and serving of the images */
  imageProvider?: ImageProvider
  /** How the provider fits the image to the size box: 'fit' keeps the whole image, 'fill' crops to the box. Default: 'fit' */
  resizingType?: ImageResizingType
  /* Priority which determines how the image is loaded; High is load first, low is load last */
  fetchPriority?: 'low' | 'high' | 'auto'
  /** Whether to show a skeleton element while image is loading */
  hasSkeletonLoader?: boolean
  /** Placeholder layout in case of broken images */
  placeHolder?: React.ReactNode
  /**
   * Callback function to handle image loading errors.
   * This function will be invoked when the image fails to load.
   */
  onImageError?: () => void
  /** Alt text. Defaults to `''` so assistive tech skips undescribed images. */
  alt?: string
}

const SkeletonElement = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 0;
`

const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--width);
  height: var(--height);
  overflow: hidden;
  border-radius: var(--border-radius);

  &.--with-placeholder {
    background-color: ${cssTheme.colors.imagePlaceholder.background};
    color: ${cssTheme.colors.imagePlaceholder.color};
  }

  &.--grid-primary {
    grid-row: 1 / 3;
    grid-column: 1 / 3;
  }

  img {
    object-fit: var(--backgroundSize);
    width: 100%;
    height: 100%;
  }
`

const defaultImageProvider: ImageProvider = (url: string) => url

const DPR_VARIANTS = [1, 2, 2.5, 2.65, 3]

export type ImageSources = {
  /** The 1x candidate, also the fallback for browsers without srcset support */
  src: string
  srcSet: string
}

/** Builds the density srcSet of `url` at `size`, one candidate per entry of `densities`. */
export const getImageSources = (
  url: string,
  size: [number, number],
  imageProvider: ImageProvider,
  {
    densities,
    resizingType
  }: {densities: number[]; resizingType?: ImageResizingType}
): ImageSources => ({
  src: imageProvider(url, size, {dpr: 1, resizingType}),
  srcSet: densities
    .map(dpr => `${imageProvider(url, size, {dpr, resizingType})} ${dpr}x`)
    .join(', ')
})

export const Image = ({
  url,
  id,
  className,
  borderRadius,
  style,
  fullWidth,
  size = 'main',
  height,
  width,
  isResponsive = false,
  backgroundSize = 'cover',
  imageProvider = defaultImageProvider,
  resizingType,
  hasSkeletonLoader = false,
  placeHolder = <ImagePlaceholder type="hotel" />,
  onImageError,
  onLoad,
  alt = '',
  ...imgProps
}: PropsType) => {
  const srcSize = SIZES[size]
  const imageWidth = width ?? srcSize[0]
  const imageHeight = height ?? srcSize[1]

  const {src: fallbackSrc, srcSet} = getImageSources(
    url,
    srcSize,
    imageProvider,
    {
      densities: DPR_VARIANTS,
      resizingType
    }
  )

  const [isLoaded, setIsLoaded] = useState(false)
  const [isErrored, setIsErrored] = useState(false)
  const shouldShowPlaceholder = !fallbackSrc || isErrored || isChromatic()

  const shouldBeResponsive = isResponsive || size === 'main'
  const isGridPrimaryImage = size === 'gridPrimary'

  const styleProps = {
    ...style,
    ['--width']: shouldBeResponsive || fullWidth ? '100%' : `${imageWidth}px`,
    ['--height']: shouldBeResponsive ? '100%' : `${imageHeight}px`,
    ['--backgroundSize']: backgroundSize,
    ['--border-radius']: borderRadius
      ? cssTheme.layout.radius[borderRadius]
      : 'none'
  }

  return (
    <Container
      id={id}
      style={styleProps}
      className={cx(
        className,
        isGridPrimaryImage && '--grid-primary',
        shouldShowPlaceholder && '--with-placeholder'
      )}
    >
      {hasSkeletonLoader && !isLoaded && !shouldShowPlaceholder && (
        <SkeletonElement>
          <SkeletonLoader height="100%" />
        </SkeletonElement>
      )}
      {shouldShowPlaceholder ? (
        placeHolder
      ) : (
        <img
          {...imgProps}
          alt={alt}
          src={fallbackSrc}
          srcSet={srcSet}
          onLoad={event => {
            setIsLoaded(true)
            onLoad?.(event)
          }}
          onError={() => {
            setIsErrored(true)
            onImageError?.()
          }}
        />
      )}
    </Container>
  )
}
